/**
 * Date and duration formatters for learner-facing timestamps.
 *
 * Chat (and other) backend timestamps are stored in UTC. Naive strings
 * (`YYYY-MM-DDTHH:mm:ss` with no `Z`/offset) are therefore parsed as UTC,
 * then formatted with local getters so the device timezone is shown.
 */

/**
 * Parses a backend timestamp as a UTC instant.
 *
 * @param {string|number|Date} value Date string, epoch millis, or Date.
 * @returns {Date|null}
 */
const parseUtcDate = (value) => {
  if (value == null || value === "" || value === "-") return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (
    typeof value === "number" ||
    (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value)))
  ) {
    const d = new Date(Number(value));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const str = String(value).trim();
  if (!str) return null;

  if (/([zZ]|GMT|[+-]\d{2}:?\d{2})/.test(str)) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const m = str.match(
    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2})(?::(\d{1,2})(?::(\d{1,2})(?:\.(\d{1,3}))?)?)?)?$/
  );

  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const hour = m[4] != null ? Number(m[4]) : 0;
    const minute = m[5] != null ? Number(m[5]) : 0;
    const second = m[6] != null ? Number(m[6]) : 0;
    const ms = m[7] != null ? Number(String(m[7]).padEnd(3, "0").slice(0, 3)) : 0;
    const d = new Date(Date.UTC(year, month - 1, day, hour, minute, second, ms));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Formats a date as a localized short calendar string in the device timezone.
 *
 * @param {string|number} value Date string or epoch millis.
 * @param {string} [lang="en"] Locale code (`uk` is mapped to `us`).
 * @returns {string}
 */
export const getFormatDate = (value, lang = "en") => {
  const date = parseUtcDate(value);
  if (!date) return "-";
  return date.toLocaleString(lang === "uk" ? "us" : lang, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Converts a date string or epoch value into a Date instance (UTC instant).
 *
 * @param {string|number} value Date string or epoch millis.
 * @returns {Date|string} Date object, or `"-"` when value is empty.
 */
export const getDateObj = (value) => {
  if (!value) return "-";
  return parseUtcDate(value) || "-";
};

/**
 * Formats a date as `MM/DD/YYYY` in the device timezone.
 *
 * @param {string|number} value Date string or epoch millis.
 * @returns {string}
 */
export const getShortDate = (value) => {
  const date = parseUtcDate(value);
  if (!date) return "-";

  const month = `0${date.getMonth() + 1}`.slice(-2);
  const dt = `0${date.getDate()}`.slice(-2);
  const year = date.getFullYear();

  return `${month}/${dt}/${year}`;
};

/**
 * Formats a date as `HH:MM:SS` in the device timezone.
 *
 * @param {string|number} value Date string or epoch millis.
 * @returns {string}
 */
export const getShortTime = (value) => {
  const date = parseUtcDate(value);
  if (!date) return "-";

  const hour = `0${date.getHours()}`.slice(-2);
  const minute = `0${date.getMinutes()}`.slice(-2);
  const second = `0${date.getSeconds()}`.slice(-2);

  return `${hour}:${minute}:${second}`;
};

/**
 * Returns the UTC midnight timestamp for the current day as a string.
 *
 * @returns {string}
 */
export const getTimeOfDate = () => {
  const date = new Date().getTime();
  const step = 1000 * 60 * 60 * 24;
  return (Math.floor(date / step) * step).toString();
};

/**
 * Formats a duration in seconds as `HH:MM:SS`.
 *
 * @param {number|string} value Duration in seconds.
 * @returns {string}
 */
export const formatTimeSpent = (value) => {
  if (!value) return "00:00:00";

  const secNum = parseInt(value, 10); // don't forget the second param
  let hours = Math.floor(secNum / 3600);
  let minutes = Math.floor((secNum - hours * 3600) / 60);
  let seconds = secNum - hours * 3600 - minutes * 60;

  if (hours < 10) {
    hours = `0${hours}`;
  }
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }
  return `${hours}:${minutes}:${seconds}`;
};
